import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOHead from '@/components/feature/SEOHead';
import { articles, categories } from '@/mocks/data';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <main>
          <Navbar />
          <div className="pt-20 pb-20 text-center">
            <h1 className="text-2xl font-heading font-bold text-foreground-950">文章未找到</h1>
            <Link to="/" className="text-primary-600 hover:text-primary-700 mt-4 inline-block cursor-pointer">返回首页</Link>
          </div>
          <Footer />
        </main>
      </>
    );
  }

  const currentCategory = categories.find((c) => c.slug === article.category);
  const categoryName = currentCategory?.name || '欧美一区';
  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.id !== article.id)
    .slice(0, 3);

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    url: `https://www.oumei1.com/article/${article.slug}`,
    datePublished: article.date,
    image: article.imageUrl,
    author: {
      '@type': 'Organization',
      name: '欧美一区',
    },
    publisher: {
      '@type': 'Organization',
      name: '欧美一区',
      url: 'https://www.oumei1.com/',
    },
    isPartOf: {
      '@type': 'WebSite',
      name: '欧美一区精选内容导航平台',
      url: 'https://www.oumei1.com/',
    },
  };

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title={article.title}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonicalPath={`/article/${article.slug}`}
        ogType="article"
        breadcrumbs={[
          { label: '首页', href: '/' },
          { label: categoryName, href: `/category/${article.category}` },
          { label: article.title },
        ]}
        jsonLd={articleJsonLd}
      />

      <main>
        <Navbar />

        <article className="pt-16 md:pt-18 bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6 py-8 md:py-10">
            <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-6 flex-wrap">
              <Link to="/" className="hover:text-foreground-800 transition-colors cursor-pointer">首页</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <Link to={`/category/${article.category}`} className="hover:text-foreground-800 transition-colors cursor-pointer">
                {categoryName}
              </Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-800 font-medium truncate max-w-[200px]">{article.title}</span>
            </nav>

            <div className="mb-6 md:mb-8">
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <span className="inline-block px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-700">
                  {categoryName}
                </span>
                {article.tags.map((tag) => (
                  <span key={tag} className="inline-block px-2.5 py-0.5 rounded-full text-xs font-medium bg-secondary-100 text-secondary-700">
                    {tag}
                  </span>
                ))}
              </div>
              <h1 className="text-xl md:text-3xl font-heading font-bold text-foreground-950 mb-4 leading-tight">
                {article.title}
              </h1>
              <div className="flex items-center gap-4 text-sm text-foreground-500">
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line"></i> {article.date}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-time-line"></i> 阅读时间 {article.readTime}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-eye-line"></i> {article.views.toLocaleString()} 次阅读
                </span>
              </div>
            </div>

            <div className="rounded-lg overflow-hidden mb-8 md:mb-10">
              <img
                src={article.imageUrl}
                alt={article.title}
                title={`${article.title} - 欧美一区精选内容推荐`}
                className="w-full aspect-video object-cover object-top"
              />
            </div>

            <div
              className="prose-custom text-sm md:text-base text-foreground-800 leading-relaxed space-y-4"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="border-t border-background-200/70 mt-10 pt-6">
              <div className="flex items-center gap-3">
                <span className="text-sm text-foreground-600">分享到：</span>
                <button className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer" aria-label="微信分享">
                  <i className="ri-wechat-line text-lg"></i>
                </button>
                <button className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer" aria-label="微博分享">
                  <i className="ri-weibo-line text-lg"></i>
                </button>
                <button className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer" aria-label="复制链接">
                  <i className="ri-link text-lg"></i>
                </button>
              </div>
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="py-12 md:py-16 bg-background-100">
            <div className="max-w-4xl mx-auto px-4 md:px-6">
              <h3 className="text-lg md:text-xl font-heading font-bold text-foreground-950 mb-6">
                相关推荐
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.id}
                    to={`/article/${ra.slug}`}
                    className="group bg-white rounded-lg overflow-hidden border border-background-200/70 hover:border-background-300/60 transition-all duration-300 cursor-pointer"
                  >
                    <div className="aspect-video overflow-hidden">
                      <img
                        src={ra.imageUrl}
                        alt={ra.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-4">
                      <h4 className="text-sm font-heading font-semibold text-foreground-950 line-clamp-2 group-hover:text-primary-600 transition-colors mb-2">
                        {ra.title}
                      </h4>
                      <span className="text-xs text-foreground-500">{ra.date}</span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        <section className="py-12 bg-primary-500">
          <div className="max-w-4xl mx-auto px-4 md:px-6 text-center">
            <h3 className="text-xl md:text-2xl font-heading font-bold text-background-50 mb-3">
              喜欢这篇文章？
            </h3>
            <p className="text-sm text-background-200 mb-6 max-w-lg mx-auto">
              欧美一区还有更多精彩内容等你发现，立即探索更多欧美一区影视、音乐与文化精选文章。
            </p>
            <Link
              to={`/category/${article.category}`}
              className="inline-block px-8 py-3 bg-background-50 text-primary-600 rounded-full text-sm font-medium hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
            >
              查看更多{categoryName}内容
            </Link>
          </div>
        </section>

        <Footer />
      </main>
    </>
  );
}